<template>
  <div v-if="indicator.eenheid">
    <p v-if="selectedVariant === 1 && hasValidData"><strong>Score:</strong>
      <span v-if="waarde">{{ waarde }}</span>
      <span v-if="!waarde">-</span>
    </p>
    <indicator-comparison v-if="showComparison" :compareYear=compareYear :currentYear=currentYear
      :historicalYear=historicalYear :compareWijkScore="rawCompareScore" :currentWijkScore="rawScore"
      :historicalWijkScore="rawHistoricalScore" :compareAvgScore=rawAvgCompareScore :currentAvgScore=rawAvgScore
      :historicalAvgScore=rawAvgHistoricalScore :isPercentage="isPercentage" :eenheid="indicator.eenheid">
    </indicator-comparison>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { Utils } from '../../ts/utils';
import config from '../constants/config';
import IndicatorComparison from "./IndicatorComparison.vue";

export default {
  name: 'SelectedMeetwaarde',

  props: {
    wijkId: Number,
    indicatorId: Number,
    selectedVariant: Number,
  },

  data() {
    return {
      indicator: {},
      wijkMeetwaarden: null,
      wijkMeetwaardenHistorical: null,
      wijkMeetwaardenCurrent: null,
      // Linechart data (always variant 1)
      linechartWijkMeetwaarden: null,
      linechartWijkMeetwaardenHistorical: null,
      linechartWijkMeetwaardenCurrent: null,
      waarde: '',
      rawScore: 0,
      rawCompareScore: 0,
      rawHistoricalScore: 0,
      rawAvgScore: 0,
      rawAvgCompareScore: 0,
      rawAvgHistoricalScore: 0,
      compareYear: config.measuredValueCompareYear,
      currentYear: config.measuredValueCurrentYear,
      historicalYear: config.measuredValueHistoricalYear,
    };
  },

  components: {
    IndicatorComparison
  },
  mounted() {
    this.fetchData();
  },

  watch: {
    indicatorId() {
      this.fetchData();
    },
    wijkId() {
      this.fetchData();
    },
    selectedVariant() {
      // When variant changes, refetch score display data but keep linechart data consistent
      this.fetchScoreData();
    }
  },

  methods: {
    ...mapMutations(['setIndicatorStore']),

    fetchData() {
      // Fetch both variant 1 data (for linechart) and selected variant data (for score display)
      Promise.all([
        // Variant 1 data for linechart (always consistent)
        Utils.Instance.getWijkenMeetwaarden(1, this.compareYear),
        Utils.Instance.getWijkenMeetwaarden(1, this.historicalYear),
        Utils.Instance.getWijkenMeetwaarden(1, this.currentYear),
        // Selected variant data for score display
        Utils.Instance.getWijkenMeetwaarden(this.selectedVariant, this.compareYear),
        Utils.Instance.getWijkenMeetwaarden(this.selectedVariant, this.historicalYear),
        Utils.Instance.getWijkenMeetwaarden(this.selectedVariant, this.currentYear)
      ]).then(([linechartCompareData, linechartHistoricalData, linechartCurrentData, scoreCompareData, scoreHistoricalData, scoreCurrentData]) => {
        // Store linechart data (always variant 1)
        this.linechartWijkMeetwaarden = linechartCompareData;
        this.linechartWijkMeetwaardenHistorical = linechartHistoricalData;
        this.linechartWijkMeetwaardenCurrent = linechartCurrentData;

        // Store score display data (selected variant)
        this.wijkMeetwaarden = scoreCompareData;
        this.wijkMeetwaardenHistorical = scoreHistoricalData;
        this.wijkMeetwaardenCurrent = scoreCurrentData;

        // Call getWaardeLabel after data is loaded
        if (this.indicatorId && this.wijkId) {
          this.getWaardeLabel();
        }
      });
    },

    getWaardeLabel() {
      Utils.Instance.getIndicator(this.indicatorId).then(value => {
        this.indicator = value;
        this.setIndicatorStore(value);

        // categorie is eigenlijk de daadwerkelijk waarde (pff)...
        // array volgorde: [
        //   waarde,
        //   categorie,
        //   schermwaarde,
        //   vergelijk categorie,
        //   gemiddelde categorie,
        //   gemiddelde vergelijk categorie
        // ]

        // Get current year (2024) data for score display (selected variant)
        if (this.wijkMeetwaardenCurrent && this.wijkMeetwaardenCurrent[this.wijkId] && this.wijkMeetwaardenCurrent[this.wijkId][this.indicatorId]) {
          const currentWaarden = this.wijkMeetwaardenCurrent[this.wijkId][this.indicatorId]
          // Array structure: [waarde, categorie, schermwaarde, vergelijk_categorie, gemiddelde_categorie, gemiddelde_vergelijking_categorie]
          // console.log('Current year data:', currentWaarden, 'for year', this.currentYear);

          // Check if we have actual data for this year - waarde should not be 0
          if (currentWaarden[0] && currentWaarden[0] !== 0) {
            this.waarde = currentWaarden[2]; // schermwaarde
          } else {
            // console.log('No actual current year data (waarde is 0)');
            this.waarde = '';
          }
        } else {
          // console.log('No current year data for wijk', this.wijkId, 'indicator', this.indicatorId);
          this.waarde = '';
        }

        // Calculate raw scores for linechart using variant 1 data
        this.calculateLinechartScores();

      });
    },

    fetchScoreData() {
      // Fetch only the score display data for the selected variant
      Promise.all([
        Utils.Instance.getWijkenMeetwaarden(this.selectedVariant, this.compareYear),
        Utils.Instance.getWijkenMeetwaarden(this.selectedVariant, this.historicalYear),
        Utils.Instance.getWijkenMeetwaarden(this.selectedVariant, this.currentYear)
      ]).then(([scoreCompareData, scoreHistoricalData, scoreCurrentData]) => {
        // Update score display data (selected variant)
        this.wijkMeetwaarden = scoreCompareData;
        this.wijkMeetwaardenHistorical = scoreHistoricalData;
        this.wijkMeetwaardenCurrent = scoreCurrentData;

        // Update score display value
        if (this.indicatorId && this.wijkId) {
          this.updateScoreDisplay();
        }
      });
    },

    updateScoreDisplay() {
      // Update only the score display value (waarde) based on selected variant data
      if (this.wijkMeetwaardenCurrent && this.wijkMeetwaardenCurrent[this.wijkId] && this.wijkMeetwaardenCurrent[this.wijkId][this.indicatorId]) {
        const currentWaarden = this.wijkMeetwaardenCurrent[this.wijkId][this.indicatorId];
        if (currentWaarden[0] && currentWaarden[0] !== 0) {
          this.waarde = currentWaarden[2]; // schermwaarde
        } else {
          this.waarde = '';
        }
      } else {
        this.waarde = '';
      }
    },

    calculateLinechartScores() {
      // Use indicator's eenheid to determine if it's a percentage, not the current year's schermwaarde
      // This ensures the multiplier is correct even when current year has no data
      const multiplier = this.isPercentage ? 100 : 1;

      // Calculate current year scores from variant 1 data
      if (this.linechartWijkMeetwaardenCurrent && this.linechartWijkMeetwaardenCurrent[this.wijkId] && this.linechartWijkMeetwaardenCurrent[this.wijkId][this.indicatorId]) {
        const currentWaarden = this.linechartWijkMeetwaardenCurrent[this.wijkId][this.indicatorId];
        if (currentWaarden[0] && currentWaarden[0] !== 0) {
          if (currentWaarden[1] !== undefined && currentWaarden[1] !== null && !isNaN(currentWaarden[1])) {
            this.rawScore = currentWaarden[1] * multiplier;
          } else {
            this.rawScore = null;
          }
          if (currentWaarden[4] !== undefined && currentWaarden[4] !== null) {
            this.rawAvgScore = currentWaarden[4] * multiplier;
          } else {
            this.rawAvgScore = null;
          }
        } else {
          this.rawScore = null;
          this.rawAvgScore = null;
        }
      } else {
        this.rawScore = null;
        this.rawAvgScore = null;
      }

      // Calculate compare year scores from variant 1 data
      if (this.linechartWijkMeetwaarden && this.linechartWijkMeetwaarden[this.wijkId] && this.linechartWijkMeetwaarden[this.wijkId][this.indicatorId]) {
        const compareWaarden = this.linechartWijkMeetwaarden[this.wijkId][this.indicatorId];
        if (compareWaarden[0] && compareWaarden[0] !== 0) {
          if (compareWaarden[1] !== undefined && compareWaarden[1] !== null && !isNaN(compareWaarden[1])) {
            this.rawCompareScore = compareWaarden[1] * multiplier;
          } else {
            this.rawCompareScore = null;
          }
          if (compareWaarden[4] !== undefined && compareWaarden[4] !== null) {
            this.rawAvgCompareScore = compareWaarden[4] * multiplier;
          } else {
            this.rawAvgCompareScore = null;
          }
        } else {
          this.rawCompareScore = null;
          this.rawAvgCompareScore = null;
        }
      } else {
        this.rawCompareScore = null;
        this.rawAvgCompareScore = null;
      }

      // Calculate historical year scores from variant 1 data
      if (this.linechartWijkMeetwaardenHistorical && this.linechartWijkMeetwaardenHistorical[this.wijkId] && this.linechartWijkMeetwaardenHistorical[this.wijkId][this.indicatorId]) {
        const historicalWaarden = this.linechartWijkMeetwaardenHistorical[this.wijkId][this.indicatorId];
        if (historicalWaarden.length >= 6) {
          if (historicalWaarden[0] && historicalWaarden[0] !== 0) {
            if (historicalWaarden[1] !== undefined && historicalWaarden[1] !== null && !isNaN(historicalWaarden[1])) {
              this.rawHistoricalScore = historicalWaarden[1] * multiplier;
            } else {
              this.rawHistoricalScore = null;
            }
            if (historicalWaarden[4] !== undefined && historicalWaarden[4] !== null) {
              this.rawAvgHistoricalScore = historicalWaarden[4] * multiplier;
            } else {
              this.rawAvgHistoricalScore = null;
            }
          } else {
            this.rawHistoricalScore = null;
            this.rawAvgHistoricalScore = null;
          }
        } else if (historicalWaarden.length >= 4) {
          // Fallback for legacy data structure
          if (historicalWaarden[0] && historicalWaarden[0] !== 0) {
            if (historicalWaarden[1] !== undefined && historicalWaarden[1] !== null && !isNaN(historicalWaarden[1])) {
              this.rawHistoricalScore = historicalWaarden[1] * multiplier;
            } else {
              this.rawHistoricalScore = null;
            }
            if (historicalWaarden[3] !== undefined && historicalWaarden[3] !== null) {
              this.rawAvgHistoricalScore = historicalWaarden[3] * multiplier;
            } else {
              this.rawAvgHistoricalScore = null;
            }
          } else {
            this.rawHistoricalScore = null;
            this.rawAvgHistoricalScore = null;
          }
        } else {
          this.rawHistoricalScore = null;
          this.rawAvgHistoricalScore = null;
        }
      } else {
        this.rawHistoricalScore = null;
        this.rawAvgHistoricalScore = null;
      }
    },

  },
  computed: {
    isPercentage() {
      // Use the indicator's eenheid field to determine if this is percentage data
      return Utils.isPercentage(this.indicator.eenheid);
    },
    showComparison() {
      const allNull = this.rawHistoricalScore === null && this.rawCompareScore === null && this.rawScore === null;

      const show = !allNull &&
        (this.rawHistoricalScore !== null || this.rawCompareScore !== null || this.rawScore !== null) &&
        (!this.rawHistoricalScore || !isNaN(this.rawHistoricalScore)) &&
        (!this.rawCompareScore || !isNaN(this.rawCompareScore)) &&
        (!this.rawScore || !isNaN(this.rawScore));

      return show;
    },
    hasValidData() {
      // Check if we have valid data by ensuring the actual value (waarden[0]) is not 0
      // and that it would produce a valid z-score (not null)
      if (!this.wijkMeetwaardenCurrent || !this.wijkMeetwaardenCurrent[this.wijkId] || !this.wijkMeetwaardenCurrent[this.wijkId][this.indicatorId]) {
        return false;
      }

      const waarden = this.wijkMeetwaardenCurrent[this.wijkId][this.indicatorId];
      const actualValue = waarden[0]; // The actual value used for z-score calculation

      // If the actual value is 0, there's no valid data
      if (actualValue === 0) {
        return false;
      }

      // Check if the z-score would be null (indicating no valid data)
      const zScore = Utils.Instance.meetwaardeToZscore(actualValue);
      return zScore !== null;
    }
  },
  filters: {
    capitalize: function (value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    formatNumber(value) {
      if (value) {
        // Check if this is distance data
        const numValue = parseFloat(value);
        const isDistanceData = numValue > 0 && numValue < 50 && numValue % 1 !== 0;

        if (isDistanceData) {
          // For distance data, use 1 decimal place
          return numValue.toFixed(1);
        } else {
          // For other data, round to whole numbers
          return numValue.toFixed(0);
        }
      }
    },
  },
};
</script>
